import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { DASH } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";
import { useUnits } from "@/lib/useUnits";
import { lbToKg } from "@/lib/units";
import { Flame, TrendingUp, Sparkles, Activity, Lock } from "lucide-react";
import CheckinForm from "@/components/CheckinForm";
import ClientShareSnapshot from "@/components/ClientShareSnapshot";
import SessionsCard from "@/components/SessionsCard";
import MySessionsCard from "@/components/MySessionsCard";
import WeaknessCard from "@/components/WeaknessCard";
import MacroCalculatorCard from "@/components/MacroCalculatorCard";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { toast } from "sonner";

const StatTile = ({ label, value, unit, accent, testid }) => (
  <div data-testid={testid} className="stat-tile">
    <div className="overline">{label}</div>
    <div className={`font-data text-4xl font-bold ${accent ? "text-blaze" : "text-white"}`}>
      {value}
      <span className="text-sm font-medium text-zinc-500 ml-1">{unit}</span>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { isImperial, fmtWeight } = useUnits();
  const [macros, setMacros] = useState(null);
  const [weights, setWeights] = useState([]);
  const [stats, setStats] = useState(null);
  const [newWeight, setNewWeight] = useState("");
  const [loadingMeals, setLoadingMeals] = useState(false);
  const [goalWeightKg, setGoalWeightKg] = useState(null);

  const load = async () => {
    try {
      const [m, w, s, p] = await Promise.all([
        api.get("/macros"),
        api.get("/weight"),
        api.get("/workouts/stats"),
        api.get("/profile"),
      ]);
      setMacros(m.data);
      setWeights(w.data);
      setStats(s.data);
      setGoalWeightKg(p.data?.profile?.goal_weight_kg ?? null);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logWeight = async () => {
    if (!newWeight) return;
    try {
      // Input is in the user's preferred unit; convert to kg before persisting.
      const kg = isImperial ? lbToKg(Number(newWeight)) : Number(newWeight);
      await api.post("/weight", { weight_kg: kg });
      setNewWeight("");
      toast.success("Weight logged · macros recalibrated");
      load();
    } catch (e) {
      toast.error("Failed to log weight");
    }
  };

  const generateMeals = async () => {
    setLoadingMeals(true);
    try {
      await api.post("/meals/generate");
      toast.success("Fresh meal plan ready");
      window.location.href = "/meals";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setLoadingMeals(false);
    }
  };

  const trend = weights.map((w) => ({
    date: new Date(w.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    weight: isImperial ? Math.round(w.weight_kg / 0.45359237 * 10) / 10 : w.weight_kg,
  }));
  const goalDisplay =
    goalWeightKg != null
      ? isImperial
        ? Math.round((goalWeightKg / 0.45359237) * 10) / 10
        : goalWeightKg
      : null;

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-blaze">/ Command center</div>
          <h1 className="font-heading text-5xl md:text-6xl uppercase mt-1">
            Hello, {user?.first_name || "Athlete"}.
          </h1>
        </div>
        <div className="hidden md:flex gap-2">
          <Link to="/share" data-testid="dash-share-link" className="ghost-btn px-4 py-2.5 text-xs inline-flex items-center gap-2">
            Share streak
          </Link>
        </div>
      </div>

      {/* Macros */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatTile testid={DASH.caloriesTile} label="Calories" value={macros?.calories ?? "—"} unit="kcal" accent />
        <StatTile testid={DASH.proteinTile} label="Protein" value={macros?.protein_g ?? "—"} unit="g" />
        <StatTile testid={DASH.carbsTile} label="Carbs" value={macros?.carbs_g ?? "—"} unit="g" />
        <StatTile testid={DASH.fatTile} label="Fats" value={macros?.fat_g ?? "—"} unit="g" />
      </div>

      {/* Soft-conversion nudge for coach-linked clients who haven't picked a plan yet.
          We removed the hard /start-trial redirect so the coach's engagement loop can
          reach the athlete — this banner keeps the "you're on Free Tier" pressure on. */}
      {user?.coach_id && user?.subscription?.status !== "active" && (
        <div
          data-testid="upgrade-nudge"
          className="mb-6 obs-card border-blaze/40 flex items-center gap-4 flex-wrap"
        >
          <div className="w-10 h-10 rounded-sm border border-blaze/60 bg-blaze/10 grid place-items-center shrink-0">
            <Lock className="h-4 w-4 text-blaze" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="overline text-blaze">You&apos;re on the free tier</div>
            <p className="text-sm text-zinc-200 mt-1">
              Meal planning, AI coach chat and auto-progression stay locked until you
              start a plan. Your coach&apos;s programme + messages are already unlocked.
            </p>
          </div>
          <Link
            to="/start-trial"
            data-testid="upgrade-nudge-cta"
            className="blaze-btn px-4 py-2 text-[11px] uppercase tracking-widest whitespace-nowrap"
          >
            Start a plan
          </Link>
        </div>
      )}

      <SessionsCard />

      <MySessionsCard />

      <WeaknessCard />

      <div className="mb-6">
        <MacroCalculatorCard onMacrosSaved={load} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 obs-card" data-testid={DASH.weightTrend}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Weight trend</div>
              <h3 className="font-heading text-2xl uppercase mt-1">Body composition signal</h3>
            </div>
            <TrendingUp className="h-5 w-5 text-blaze" />
          </div>
          {trend.length > 1 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", borderRadius: 4 }} />
                  {goalDisplay != null && (
                    <ReferenceLine
                      y={goalDisplay}
                      stroke="#FACC15"
                      strokeDasharray="6 4"
                      ifOverflow="extendDomain"
                      label={{ value: `Goal ${goalDisplay}`, fill: "#FACC15", fontSize: 10, position: "insideTopRight" }}
                    />
                  )}
                  <Line type="monotone" dataKey="weight" stroke="#FF3B30" strokeWidth={2} dot={{ fill: "#FF3B30", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 grid place-items-center text-zinc-500 text-sm uppercase tracking-widest">
              Log more weight entries to see your trend
            </div>
          )}
          <div className="mt-4 flex items-center gap-3">
            <input
              type="number"
              step="0.1"
              placeholder={isImperial ? "Today's weight (lb)" : "Today's weight (kg)"}
              value={newWeight}
              onChange={(e) => setNewWeight(e.target.value)}
              className="flex-1 bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
            />
            <button onClick={logWeight} data-testid={DASH.logWeight} className="blaze-btn px-5 py-2 text-xs">Log</button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="obs-card">
            <div className="flex items-center gap-3 mb-3">
              <Activity className="h-5 w-5 text-blaze" />
              <div className="overline">Training pulse</div>
            </div>
            <div className="font-data text-5xl font-bold">{stats?.total_workouts ?? 0}</div>
            <div className="text-zinc-400 text-sm mt-1">Total workouts logged</div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="overline">PRs</div>
                <div className="font-data text-lg">{stats ? Object.keys(stats.personal_records).length : 0}</div>
              </div>
              <div>
                <div className="overline">Volume (last)</div>
                <div className="font-data text-lg">
                  {(() => {
                    const lastKg = stats?.volume_trend?.slice(-1)?.[0]?.volume ?? 0;
                    if (isImperial) {
                      return `${Math.round(lastKg / 0.45359237).toLocaleString()} lbs`;
                    }
                    return `${Number(lastKg).toFixed(0)} kg`;
                  })()}
                </div>
              </div>
            </div>
            {stats?.total_workouts > 0 && (
              <div className="mt-4">
                <ClientShareSnapshot
                  firstName={user?.first_name || "Athlete"}
                  stats={stats}
                  weights={weights}
                  isImperial={isImperial}
                />
              </div>
            )}
          </div>

          <div className="obs-card">
            <div className="overline mb-2">AI meal plan</div>
            <h4 className="font-heading text-2xl uppercase mb-2">Daily plan, on demand</h4>
            <p className="text-zinc-400 text-sm mb-4">High-protein, allergy-aware, macro-matched.</p>
            <button onClick={generateMeals} data-testid={DASH.generateMeals} disabled={loadingMeals} className="blaze-btn w-full py-2.5 text-xs disabled:opacity-50">
              {loadingMeals ? "Generating..." : "Generate today's plan"}
            </button>
          </div>
        </div>
      </div>

      {macros?.reasoning && (
        <div className="obs-card">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="h-4 w-4 text-blaze" />
            <div className="overline">AI Reasoning</div>
          </div>
          <p className="text-zinc-300 leading-relaxed text-sm">{macros.reasoning}</p>
        </div>
      )}

      {/* Weekly check-in (shown when client has a linked coach) */}
      {user?.coach_id && (
        <div className="space-y-3">
          <CheckinForm onSaved={load} />
        </div>
      )}
    </div>
  );
}
